import api from "lib/axios";
import type { ApiEnvelope } from "services/apiTypes";
import { normalizeChatMessage } from "utils/normalizeChatMessagesResponse";

export type CourseChatScope = "student" | "instructor" | "admin";

export type CourseChatSender = {
  id: number;
  name: string | null;
  role: string;
  avatar_url: string | null;
};

export type CourseChatAttachment = {
  url: string | null;
  path?: string | null;
  name: string | null;
  mime: string | null;
  size: number | null;
  duration_seconds: number | null;
};

export type CourseChatMessageStatus = "uploading" | "sending" | "sent" | "failed";

export type CourseChatMessage = {
  id: string;
  course_id: number;
  sender: CourseChatSender;
  message_type: "text" | "image" | "file" | "voice" | "system";
  body: string | null;
  attachment: CourseChatAttachment | null;
  client_message_id?: string | null;
  is_mine: boolean;
  read_count: number | null;
  created_at: string | null;
  created_at_millis?: number;
  created_at_human: string | null;
  is_deleted: boolean;
  /** Local-only optimistic fields (never sourced from Firestore). */
  status?: CourseChatMessageStatus;
  local?: boolean;
  local_created_at_millis?: number;
  local_preview_url?: string | null;
  upload_progress?: number;
};

export type CourseChatSettings = {
  course_id: number;
  is_muted: boolean;
  muted_by: { id: number; name: string | null; avatar_url: string | null } | null;
  muted_at: string | null;
  mute_reason: string | null;
};

export type CourseChatBootstrapResponse = {
  course: { id: number; title: string };
  firebase: {
    uid: string;
    course_chat_path: string;
    messages_path: string;
  };
  settings: { is_muted: boolean };
  permissions: { can_read: boolean; can_send: boolean; can_mute: boolean };
  /** Per-user mute of push notifications for this course only. */
  notifications_muted?: boolean;
};

function assertApiEnvelope(data: unknown): asserts data is ApiEnvelope<unknown> {
  if (data == null || typeof data !== "object" || !("status" in data)) {
    throw new Error("Invalid API response — check REACT_APP_API_URL points to the Laravel API.");
  }
}

function basePath(scope: CourseChatScope, courseId: number | string): string {
  if (scope === "admin") return `/mcp/courses/${courseId}/chat`;
  if (scope === "instructor") return `/instructors/courses/${courseId}/chat`;
  return `/students/courses/${courseId}/chat`;
}

function unwrapResult<T>(envelope: ApiEnvelope<unknown>): T {
  const result = envelope.result as { data?: T } | T;
  if (result && typeof result === "object" && "data" in result) {
    return result.data as T;
  }
  return result as T;
}

function defaultSettings(courseId: number | string): CourseChatSettings {
  return {
    course_id: Number(courseId),
    is_muted: false,
    muted_by: null,
    muted_at: null,
    mute_reason: null,
  };
}

function normalizeChatSettings(
  envelope: ApiEnvelope<unknown> | undefined,
  courseId: number | string
): CourseChatSettings {
  const payload = envelope?.result;
  if (payload && typeof payload === "object" && "data" in payload) {
    const nested = (payload as { data?: CourseChatSettings }).data;
    if (nested && typeof nested === "object" && "is_muted" in nested) {
      return nested;
    }
  }
  if (payload && typeof payload === "object" && "is_muted" in payload) {
    return payload as CourseChatSettings;
  }
  return defaultSettings(courseId);
}

export const courseChatService = {
  async bootstrap(scope: CourseChatScope, courseId: number | string): Promise<CourseChatBootstrapResponse> {
    const res = await api.get<ApiEnvelope<CourseChatBootstrapResponse>>(
      `${basePath(scope, courseId)}/bootstrap`,
      { meta: { toastSuccess: false, toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    if (res.data.status !== "success") {
      throw new Error((res.data as ApiEnvelope<unknown> & { message?: string }).message || "Failed to bootstrap chat");
    }
    return unwrapResult<CourseChatBootstrapResponse>(res.data);
  },

  async getFirebaseToken(): Promise<{
    firebase_token: string;
    firebase_uid: string;
    expires_in: number;
    claims: { role: string; app_user_id: number; guard: string };
  }> {
    const res = await api.get<ApiEnvelope<{
      firebase_token: string;
      firebase_uid: string;
      expires_in: number;
      claims: { role: string; app_user_id: number; guard: string };
    }>>("/common/firebase/token", { meta: { toastSuccess: false, toastError: false } as any });
    assertApiEnvelope(res.data);
    return unwrapResult(res.data);
  },

  async resolveAttachmentUrl(scope: CourseChatScope, courseId: number | string, messageId: string): Promise<string> {
    const res = await api.get<ApiEnvelope<{ url: string }>>(
      `${basePath(scope, courseId)}/messages/${messageId}/attachment-url`,
      { meta: { toastSuccess: false, toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    const payload = unwrapResult<{ url: string }>(res.data);
    if (!payload?.url) throw new Error("Missing attachment URL");
    return payload.url;
  },

  async deleteMessage(
    scope: CourseChatScope,
    courseId: number | string,
    messageId: string
  ): Promise<{ message_id: string; is_deleted: boolean }> {
    const res = await api.delete<ApiEnvelope<{ message_id: string; is_deleted: boolean }>>(
      `${basePath(scope, courseId)}/messages/${messageId}`,
      { meta: { toastSuccess: false, toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    if (res.data.status !== "success") {
      throw new Error(
        (res.data as ApiEnvelope<unknown> & { message?: string }).message || "Failed to delete message"
      );
    }
    return unwrapResult<{ message_id: string; is_deleted: boolean }>(res.data);
  },

  async sendText(scope: CourseChatScope, courseId: number | string, body: string, clientMessageId?: string) {
    const res = await api.post<ApiEnvelope<CourseChatMessage>>(
      `${basePath(scope, courseId)}/messages`,
      { message_type: "text", body, client_message_id: clientMessageId },
      { meta: { toastSuccess: false, toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    const message = normalizeChatMessage(res.data);
    if (!message) {
      throw new Error("Invalid chat message response");
    }
    return message;
  },

  async sendAttachment(
    scope: CourseChatScope,
    courseId: number | string,
    messageType: "image" | "file" | "voice",
    file: File | Blob,
    options?: {
      body?: string;
      voice_duration_seconds?: number;
      fileName?: string;
      clientMessageId?: string;
      onUploadProgress?: (percent: number) => void;
    }
  ) {
    const form = new FormData();
    form.append("message_type", messageType);
    if (options?.body) form.append("body", options.body);
    if (options?.voice_duration_seconds != null) {
      form.append("voice_duration_seconds", String(options.voice_duration_seconds));
    }
    if (options?.clientMessageId) form.append("client_message_id", options.clientMessageId);
    const name = options?.fileName ?? (file instanceof File ? file.name : "file");
    form.append("file", file, name);

    const res = await api.post<ApiEnvelope<CourseChatMessage>>(
      `${basePath(scope, courseId)}/messages`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        meta: { toastSuccess: false, toastError: false } as any,
        onUploadProgress: options?.onUploadProgress
          ? (e) => {
              if (!e.total) return;
              options.onUploadProgress?.(Math.min(100, Math.round((e.loaded / e.total) * 100)));
            }
          : undefined,
      }
    );
    assertApiEnvelope(res.data);
    const message = normalizeChatMessage(res.data);
    if (!message) {
      throw new Error("Invalid chat message response");
    }
    return message;
  },

  async heartbeat(courseId: number | string, isActive = true): Promise<void> {
    await api.post(
      "/common/firebase-chat/heartbeat",
      { course_id: Number(courseId), is_active: isActive },
      { meta: { toastSuccess: false, toastError: false } as any }
    );
  },

  async leaveChat(courseId: number | string): Promise<void> {
    await api.post(
      "/common/firebase-chat/inactive",
      { course_id: Number(courseId) },
      { meta: { toastSuccess: false, toastError: false } as any }
    );
  },

  async mute(courseId: number | string, muteReason?: string) {
    const res = await api.put<ApiEnvelope<CourseChatSettings>>(
      `/instructors/courses/${courseId}/chat/mute`,
      muteReason ? { mute_reason: muteReason } : {},
      { meta: { toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    return normalizeChatSettings(res.data, courseId);
  },

  async unmute(courseId: number | string) {
    const res = await api.put<ApiEnvelope<CourseChatSettings>>(
      `/instructors/courses/${courseId}/chat/unmute`,
      {},
      { meta: { toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    return normalizeChatSettings(res.data, courseId);
  },

  async setNotificationsMuted(
    scope: CourseChatScope,
    courseId: number | string,
    muted: boolean
  ): Promise<{ notifications_muted: boolean; course_id: number }> {
    if (scope === "admin") {
      throw new Error("Notification mute is not available for admin chat");
    }
    const res = await api.put<ApiEnvelope<{ notifications_muted: boolean; course_id: number }>>(
      `${basePath(scope, courseId)}/notification-mute`,
      { muted },
      { meta: { toastSuccess: false, toastError: false } as any }
    );
    assertApiEnvelope(res.data);
    if (res.data.status !== "success") {
      throw new Error(
        (res.data as ApiEnvelope<unknown> & { message?: string }).message ||
          "Failed to update notification mute"
      );
    }
    return unwrapResult<{ notifications_muted: boolean; course_id: number }>(res.data);
  },
};
